import React, { useMemo, useCallback, useReducer } from 'react';

import * as http from '../../http';
import { buildAuthorizedGetRequest } from '../../http/request-templates';
import { ProfileContext } from './ProfileContext';
import { Profile } from './ProfileModels';

import profileReducer, {
  ERR_GET_PROFILE,
  profileReducerInitialState,
  RCV_GET_PROFILE,
  REQ_GET_PROFILE,
} from './profileReducer';
import useAuth from '../Auth/useAuth';

interface ProfileProviderProps {
  children: React.ReactNode;
}

const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const { getAccessToken } = useAuth();

  const [profileState, dispatch] = useReducer(profileReducer, profileReducerInitialState);

  const getProfile = useCallback(async () => {
    try {
      dispatch({ type: REQ_GET_PROFILE });
      const response = await http.rawJson<Profile>(
        `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/profile`,
        buildAuthorizedGetRequest(await getAccessToken())
      );

      // API:et svarar ibland 200 med en felkropp. Utan den här kontrollen
      // hamnar det objektet i state, ser ut som en profil utan uppgifter och
      // hämtas aldrig om.
      if (!response?.email) {
        throw new Error('Profile response is missing email');
      }

      dispatch({ type: RCV_GET_PROFILE, profile: response });
    } catch (e) {
      dispatch({ type: ERR_GET_PROFILE });
    }
  }, [getAccessToken]);

  const value = useMemo(
    () => ({
      ...profileState,
      getProfile,
    }),
    [getProfile, profileState]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export default ProfileProvider;
